import {
  MAXIMUM_REPORT_DURATION_SECONDS,
  MAXIMUM_REPORTING_RANGE_DAYS,
  type FilterCatalog,
  type ReportFilterStateV1,
  type SubgroupCatalog,
} from "@meta-video/contracts";
import {
  Button,
  Fieldset,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";

import {
  AdNameSearchField,
  useAdNameSearchController,
  useAdNameSearchContext,
  type AdNameSearchContextValue,
} from "./AdNameSearchInput.js";
import { DurationFilter } from "./DurationFilter.js";
import { MetricRangeFilter } from "./MetricRangeFilter.js";
import { useAcknowledgedFilterChanges } from "./useAcknowledgedFilterChanges.js";
import { cascadeBrandSelection } from "./useFilterState.js";

export interface FilterBarProps {
  readonly catalog: FilterCatalog;
  readonly filters: ReportFilterStateV1;
  readonly maxDurationSeconds?: number;
  readonly onChange: (filters: ReportFilterStateV1) => boolean | void;
  readonly subgroupCatalogs: Readonly<Record<string, SubgroupCatalog>>;
  readonly unknownDurationCount: number | null | undefined;
}

function uniqueAccountOptions(catalog: FilterCatalog, brandIds: readonly string[]) {
  const byId = new Map<string, { label: string; value: string }>();
  const selectedBrandIds = new Set(brandIds);
  for (const brand of catalog.brands) {
    if (!selectedBrandIds.has(brand.id)) continue;
    for (const account of brand.accounts) {
      if (byId.has(account.id)) continue;
      byId.set(account.id, {
        label: account.isPresent ? account.name : `${account.name} (not currently present)`,
        value: account.id,
      });
    }
  }
  return [...byId.values()].sort((left, right) => left.label.localeCompare(right.label));
}

function shiftUtcDate(isoDate: string, days: number): string {
  const timestamp = Date.parse(`${isoDate}T00:00:00.000Z`);
  return new Date(timestamp + days * 86_400_000).toISOString().slice(0, 10);
}

function subgroupOptionValue(
  brandId: string,
  groupId: string,
  valueId: string,
): string {
  return JSON.stringify([brandId, groupId, valueId]);
}

export function FilterBar({
  ...props
}: FilterBarProps) {
  const shared = useAdNameSearchContext();
  return shared === null
    ? <StandaloneFilterBar {...props} />
    : <FilterBarView {...props} search={shared} />;
}

function StandaloneFilterBar(props: FilterBarProps) {
  const filterChanges = useAcknowledgedFilterChanges({
    filters: props.filters,
    onChange: props.onChange,
  });
  const controller = useAdNameSearchController({
    filterChanges,
    filters: props.filters,
  });
  return (
    <FilterBarView
      {...props}
      search={{ controller, filterChanges }}
    />
  );
}

function FilterBarView({
  catalog,
  filters,
  maxDurationSeconds = MAXIMUM_REPORT_DURATION_SECONDS,
  search,
  subgroupCatalogs,
  unknownDurationCount,
}: Omit<FilterBarProps, "onChange"> & {
  readonly search: AdNameSearchContextValue;
}) {
  const changeFilters = search.filterChanges.changeFilters;
  const accounts = useMemo(
    () => uniqueAccountOptions(catalog, filters.brandIds),
    [catalog, filters.brandIds],
  );
  const subgroupControls = useMemo(() => {
    const groupsByName = new Map<string, SubgroupCatalog[number][]>();
    for (const brandId of filters.brandIds) {
      for (const group of subgroupCatalogs[brandId] ?? []) {
        const namedGroups = groupsByName.get(group.name);
        if (namedGroups) namedGroups.push(group);
        else groupsByName.set(group.name, [group]);
      }
    }
    return [...groupsByName].map(([name, groups]) => ({ groups, name }));
  }, [filters.brandIds, subgroupCatalogs]);
  const customDate = filters.date.preset === "custom" ? filters.date : null;
  const [customDateDraft, setCustomDateDraft] = useState(() => ({
    from: customDate?.from ?? "",
    to: customDate?.to ?? "",
  }));
  useEffect(() => {
    if (customDate) {
      setCustomDateDraft({ from: customDate.from, to: customDate.to });
    }
  }, [customDate?.from, customDate?.to]);
  const utcYesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  const earliestCustomFrom = customDateDraft.to
    ? shiftUtcDate(customDateDraft.to, -(MAXIMUM_REPORTING_RANGE_DAYS - 1))
    : null;
  const latestCustomTo = customDateDraft.from
    ? [
        utcYesterday,
        shiftUtcDate(customDateDraft.from, MAXIMUM_REPORTING_RANGE_DAYS - 1),
      ].sort()[0]!
    : null;
  const customDateDraftValid = customDate !== null
    && customDateDraft.from !== ""
    && customDateDraft.to !== ""
    && customDateDraft.from <= customDateDraft.to
    && customDateDraft.from >= earliestCustomFrom!
    && customDateDraft.to <= latestCustomTo!
    && customDateDraft.to <= utcYesterday;
  const customDateDraftChanged = customDate !== null
    && (
      customDateDraft.from !== customDate.from
      || customDateDraft.to !== customDate.to
    );

  return (
    <section aria-labelledby="report-filters-heading" className="filter-bar">
      <Stack gap="md">
        <div>
          <h2 id="report-filters-heading">Report filters</h2>
          <Text c="dimmed" size="sm">Filters are kept in the URL so this view can be shared.</Text>
        </div>

        <Stack gap="md">
          <div data-filter-section="name">
            <AdNameSearchField
              controller={search.controller}
              label="Ad name contains"
            />
          </div>
          <div data-filter-section="brands">
            <MultiSelect
              aria-label="Brands"
              clearable
              data={catalog.brands.map((brand) => ({ label: brand.name, value: brand.id }))}
              label="Brands"
              onChange={(brandIds) => changeFilters(
                cascadeBrandSelection(filters, brandIds, catalog),
              )}
              searchable
              value={[...filters.brandIds]}
            />
          </div>
          <div data-filter-section="date">
            <Select
              allowDeselect={false}
              aria-label="Date range"
              data={[
                { label: "Last 7 complete days", value: "7" },
                { label: "Last 30 complete days", value: "30" },
                { label: "Last 90 complete days", value: "90" },
                { label: "Custom range", value: "custom" },
              ]}
              label="Date range"
              onChange={(preset) => {
                if (preset === "7" || preset === "30" || preset === "90") {
                  changeFilters({
                    ...filters,
                    date: { preset: Number(preset) as 7 | 30 | 90 },
                  });
                } else if (preset === "custom") {
                  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
                  changeFilters({
                    ...filters,
                    date: { from: yesterday, preset: "custom", to: yesterday },
                  });
                }
              }}
              value={String(filters.date.preset)}
            />
          </div>
        </Stack>

        {customDate ? (
          <Group align="end" data-filter-section="date" grow>
            <TextInput
              aria-label="Custom range start"
              label="From"
              max={customDateDraft.to && customDateDraft.to < utcYesterday
                ? customDateDraft.to
                : utcYesterday}
              min={earliestCustomFrom ?? undefined}
              onChange={(event) => {
                const from = event.currentTarget.value;
                setCustomDateDraft((current) => ({
                  ...current,
                  from,
                }));
              }}
              type="date"
              value={customDateDraft.from}
            />
            <TextInput
              aria-label="Custom range end"
              label="To"
              max={latestCustomTo ?? utcYesterday}
              min={customDateDraft.from || undefined}
              onChange={(event) => {
                const to = event.currentTarget.value;
                setCustomDateDraft((current) => ({
                  ...current,
                  to,
                }));
              }}
              type="date"
              value={customDateDraft.to}
            />
            <Button
              aria-label="Apply custom date range"
              disabled={!customDateDraftValid || !customDateDraftChanged}
              onClick={() => changeFilters({
                ...filters,
                date: { ...customDateDraft, preset: "custom" },
              })}
              type="button"
            >
              Apply dates
            </Button>
          </Group>
        ) : null}

        <Fieldset data-filter-section="first-seen" legend="First seen" p="xs">
          <Group grow>
            <TextInput
              aria-label="First seen from"
              label="From"
              max={filters.firstSeenTo ?? undefined}
              onChange={(event) => {
                const firstSeenFrom = event.currentTarget.value || null;
                if (
                  firstSeenFrom !== null
                  && filters.firstSeenTo !== null
                  && firstSeenFrom > filters.firstSeenTo
                ) return;
                changeFilters({ ...filters, firstSeenFrom });
              }}
              type="date"
              value={filters.firstSeenFrom ?? ""}
            />
            <TextInput
              aria-label="First seen to"
              label="To"
              min={filters.firstSeenFrom ?? undefined}
              onChange={(event) => {
                const firstSeenTo = event.currentTarget.value || null;
                if (
                  firstSeenTo !== null
                  && filters.firstSeenFrom !== null
                  && firstSeenTo < filters.firstSeenFrom
                ) return;
                changeFilters({ ...filters, firstSeenTo });
              }}
              type="date"
              value={filters.firstSeenTo ?? ""}
            />
          </Group>
        </Fieldset>

        {subgroupControls.length > 0 ? (
          <Fieldset legend="Subgroups" p="xs">
            <Stack gap="xs">
              {subgroupControls.map(({ groups: sameNamedGroups, name }) => {
                const selected = sameNamedGroups.flatMap((group) => {
                  const selection = filters.groupSelections.find(
                    (candidate) => candidate.brandId === group.brandId
                      && candidate.groupId === group.id,
                  );
                  return [
                    ...(selection?.valueIds ?? []).map((valueId) =>
                      subgroupOptionValue(group.brandId, group.id, valueId)),
                    ...(selection?.includeUnassigned
                      ? [subgroupOptionValue(group.brandId, group.id, group.unassigned.id)]
                      : []),
                  ];
                });
                return (
                  <div data-filter-section="subgroups" key={name}>
                    <MultiSelect
                      aria-label={`${name} subgroups`}
                      clearable
                      data={sameNamedGroups.map((group) => ({
                        group: catalog.brands.find((brand) => brand.id === group.brandId)?.name
                          ?? group.brandId,
                        items: [
                          ...group.values.map((value) => ({
                            label: value.name,
                            value: subgroupOptionValue(group.brandId, group.id, value.id),
                          })),
                          {
                            label: group.unassigned.name,
                            value: subgroupOptionValue(
                              group.brandId,
                              group.id,
                              group.unassigned.id,
                            ),
                          },
                        ],
                      }))}
                      label={name}
                      onChange={(values) => {
                        const selectedValues = new Set(values);
                        const isSameNamedGroup = (candidate: ReportFilterStateV1["groupSelections"][number]) =>
                          sameNamedGroups.some((group) => candidate.brandId === group.brandId
                            && candidate.groupId === group.id);
                        const nextSelections = sameNamedGroups.flatMap((group) => {
                          const valueIds = group.values
                            .filter((value) => selectedValues.has(
                              subgroupOptionValue(group.brandId, group.id, value.id),
                            ))
                            .map((value) => value.id);
                          const includeUnassigned = selectedValues.has(
                            subgroupOptionValue(group.brandId, group.id, group.unassigned.id),
                          );
                          return valueIds.length === 0 && !includeUnassigned
                            ? []
                            : [{
                                brandId: group.brandId,
                                groupId: group.id,
                                includeUnassigned,
                                valueIds,
                              }];
                        });
                        changeFilters({
                          ...filters,
                          groupSelections: [
                            ...filters.groupSelections.filter((candidate) => !isSameNamedGroup(candidate)),
                            ...nextSelections,
                          ],
                        });
                      }}
                      searchable
                      value={selected}
                    />
                  </div>
                );
              })}
              <Text c="dimmed" size="xs">
                Each group includes an explicit (Unassigned) value for ads that match no rule.
              </Text>
            </Stack>
          </Fieldset>
        ) : null}

        <div data-filter-section="duration">
          <DurationFilter
            maxSeconds={maxDurationSeconds}
            onChange={({ max, min }) => changeFilters({
              ...filters,
              durationMax: max,
              durationMin: min,
            })}
            unknownCount={unknownDurationCount}
            value={{ max: filters.durationMax, min: filters.durationMin }}
          />
        </div>

        <div data-filter-section="spend">
          <MetricRangeFilter
            label="Spend"
            onChange={({ max, min }) => changeFilters({
              ...filters,
              spendMax: max,
              spendMin: min,
            })}
            value={{ max: filters.spendMax, min: filters.spendMin }}
          />
        </div>

        <div data-filter-section="roas">
          <MetricRangeFilter
            label="ROAS"
            onChange={({ max, min }) => changeFilters({
              ...filters,
              roasMax: max,
              roasMin: min,
            })}
            value={{ max: filters.roasMax, min: filters.roasMin }}
          />
        </div>

        <div data-filter-section="accounts">
          <MultiSelect
            aria-label="Ad accounts"
            clearable
            data={accounts}
            disabled={filters.brandIds.length === 0}
            label="Ad accounts"
            onChange={(accountIds) => changeFilters({
              ...filters,
              accountIds: accountIds.length === 0 ? null : accountIds,
            })}
            placeholder={filters.brandIds.length === 0
              ? "Select a brand first"
              : "All accounts in selected brands"}
            searchable
            value={filters.accountIds === null ? [] : [...filters.accountIds]}
          />
        </div>
      </Stack>
    </section>
  );
}
